import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { buildProviders, Coerce } from '@kalidea/kaligraphi';
import { Observable, of } from 'rxjs';
import { startWith, tap } from 'rxjs/operators';
import { CustomFormControlAccessWithLoaders } from 'src/app/_core/tools/custom-form-control-access';
import { LoginLists, UserState } from 'src/app/_user/store/user/user.state';
import { arrayOnlyOne } from 'src/app/utils/array.utils';
import { FormValidator } from 'src/app/validators/form-validator';
import { CaisseDto, CeDelegationDto, PointVenteDto, StockDto } from 'src/graphql/generated-types';

export interface UserDelegation {
    delegationId: number;
    sellPointId: number;
    deskId: number;
    stockId: number;
}

interface DelegationPickerForm {
    delegationId: FormControl<number | null>;
    sellPointId: FormControl<number | null>;
    deskId: FormControl<number | null>;
    stockId: FormControl<number | null>;
}

@Component({
    selector: 'app-user-delegation-picker',
    templateUrl: './user-delegation-picker.component.html',
    styleUrls: ['./user-delegation-picker.component.sass'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: buildProviders(UserDelegationPickerComponent),
    standalone: false
})
export class UserDelegationPickerComponent extends CustomFormControlAccessWithLoaders implements OnInit {

    @Input()
    @Coerce('boolean')
    isThemeReverse = false;

    form: FormGroup<DelegationPickerForm>;

    displayedDelegationsList: CeDelegationDto[] = [];

    displayedSellsPointList: PointVenteDto[] = [];

    displayedDesksList: CaisseDto[] = [];

    displayedStocksList: StockDto[] = [];

    constructor(protected readonly cdr: ChangeDetectorRef,
                private readonly fb: FormBuilder) {
        super(cdr);
    }

    get theme(): string {
        return this.isThemeReverse ? 'reverse' : '';
    }

    private get sellPointIdControl(): AbstractControl<number, number> {
        return this.form.get('sellPointId');
    }

    private get loginLists(): LoginLists {
        return this.store.selectSnapshot(UserState.lists);
    }

    writeValue(value: UserDelegation): void {
        // émission de l'event nécessaire pour désactiver les champs lors d'un pré-remplissage
        this.form.patchValue(value);
        super.writeValue(value);
    }

    validate(c: AbstractControl): Observable<{delegationpicker: boolean} | null> {
        return of(this.form.valid ? null : {delegationpicker: true});
    }

    // <editor-fold defaultstate="collapsed" desc="Handle delegation change">

    /**
     * Filter displayed sells points, desks and stocks according to selected delegation.
     * Used when the delegation has been changed.
     */
    private filterDisplayedListsOnDelegationChange(delegationId: number): void {
        const {sellsPointList, desksList, stocksList} = this.loginLists;

        this.displayedSellsPointList = sellsPointList.filter(e => e.CEDelegation.Id === delegationId);

        // filter both desks list and stocks list according to selected sell point
        this.displayedDesksList = desksList.filter(desk => {
            return this.displayedSellsPointList.some(sellPoint => sellPoint.ID === desk.PV.ID);
        });

        this.displayedStocksList = stocksList.filter(stock => {
            return this.displayedSellsPointList.some(sellPoint => sellPoint.ID === stock.PV.ID);
        });
    }

    /**
     * Set default values of sell point, desk and stock.
     * Used when the delegation has changed.
     */
    private selectDefaultValuesOnDelegationChange(): void {
        const currentSellPoint = this.snapshot(UserState.currentSellPoint);
        if (arrayOnlyOne(this.displayedSellsPointList) || currentSellPoint) {
            const sellPointValue = arrayOnlyOne(this.displayedSellsPointList)
                ? this.displayedSellsPointList[0].ID
                : currentSellPoint.ID;

            this.sellPointIdControl.patchValue(sellPointValue, {emitEvent: false});

            if (arrayOnlyOne(this.displayedSellsPointList)) {
                this.sellPointIdControl.disable({emitEvent: false});
            }
        }

        // set default value for desk and stock
        this.selectDefaultValuesOnChange();
    }

    // </editor-fold>

    // <editor-fold defaultstate="collapsed" desc="Handle sell point change">

    /**
     * Filter displayed desks and stocks according to selected sell point.
     * Used when the sell point has changed.
     */
    private filterDisplayedListsOnSellPointChange(sellPointId: number): void {
        const {desksList, stocksList} = this.loginLists;

        this.displayedDesksList = desksList.filter(desk => desk.PV.ID === sellPointId);
        this.displayedStocksList = stocksList.filter(desk => desk.PV.ID === sellPointId);
    }

    // </editor-fold>

    // <editor-fold defaultstate="collapsed" desc="Common to delegation change and sell point change">

    /**
     * Set default values for desk and stock.
     * Used when delegation or sell point has changed.
     */
    private selectDefaultValuesOnChange(): void {
        Object.entries({
            deskId: { list: this.displayedDesksList, defaultValue: this.snapshot(UserState.currentCaisse) },
            stockId: { list: this.displayedStocksList, defaultValue: this.snapshot(UserState.currentStock) }
        }).forEach(([formControlName, valuesObj]) => {
            const control = this.form.get(formControlName);

            if(arrayOnlyOne(valuesObj.list) || valuesObj.defaultValue) {
                const selectedValue = arrayOnlyOne(valuesObj.list) ? valuesObj.list[0].Id : valuesObj.defaultValue.Id;
                control.patchValue(selectedValue, {emitEvent: false});

                if (arrayOnlyOne(valuesObj.list)) {
                    control.disable({emitEvent: false});
                }

            } else {
                control.reset(null, {emitEvent: false});
                control.enable({emitEvent: false});
            }
        });
    }

    // </editor-fold>

    private initSubscriptions(): void {
        this.watch$ = this.form.get('delegationId').valueChanges.pipe(
            tap(delegationId => {
                this.filterDisplayedListsOnDelegationChange(delegationId);
                this.selectDefaultValuesOnDelegationChange();
            })
        );

        this.watch$ = this.sellPointIdControl.valueChanges.pipe(
            tap(sellPointId => {
                this.filterDisplayedListsOnSellPointChange(sellPointId);
                this.selectDefaultValuesOnChange();
            })
        );
    }

    ngOnInit(): void {
        this.form = this.fb.group({
            delegationId: this.fb.control(null, [FormValidator.isNotNull]),
            sellPointId: this.fb.control(null, [FormValidator.isNotNull]),
            deskId: this.fb.control(null, [FormValidator.isNotNull]),
            stockId: this.fb.control(null, [FormValidator.isNotNull])
        });

        const {
            delegationsList,
            sellsPointList,
            desksList,
            stocksList
        } = this.loginLists;

        this.displayedDelegationsList = delegationsList;
        this.displayedSellsPointList = sellsPointList;
        this.displayedDesksList = desksList;
        this.displayedStocksList = stocksList;

        this.initSubscriptions();

        if (arrayOnlyOne(this.displayedDelegationsList)) {
            this.form.get('delegationId').disable({emitEvent: false});
        }

        this.watch$ = this.form.valueChanges.pipe(
            startWith(() => null),
            tap(() => this.notifyUpdate(this.form.getRawValue()))
        );
    }

}
